import { Dispatch } from "redux";

import {
	BulkDeleteRecordsRequest,
	BulkUpdateRecordsRequest,
	CustomView,
	CustomViewResponse,
	CustomViewsMetadata,
	ListPagePostRequest
} from "@/Contracts/UI";
import { ClientContact, ClientContactsResponse } from "@/Contracts/Contacts";
import { RecentOpportunityItemView } from "@/Contracts/Opportunity";
import { RecentDocumentItemView } from "@/Contracts/Documents";
import { GlobalState } from "@/Reducers/interface";
import {
	getContactListPost,
	getAllContactViews,
	getContactViewMetadata,
	createContactView as createContactViewService,
	updateContactView as updateContactViewService,
	deleteContactView as deleteContactViewService,
	bulkDeleteContacts,
	bulkUpdateContacts
} from "@/Services/Contacts";
import { getRecentOpportunitiesByClientUserIds } from "@/Services/Opportunity";
import { getRecentDocumentsByClientUserIds } from "@/Services/Documents";
import { areFiltersChanged } from "@/Utils";

export enum ContactsActionTypes {
	SHOW_CONTACTS_LOADER = "CONTACTS_SHOW_LOADER",
	HIDE_CONTACTS_LOADER = "CONTACTS_HIDE_LOADER",

	FETCH_CONTACTS_REQUESTED = "CONTACTS_FETCH_REQUESTED",
	FETCH_CONTACTS_SUCCESSFUL = "CONTACTS_FETCH_SUCCESSFUL",
	FETCH_CONTACTS_FAILED = "CONTACTS_FETCH_FAILED",

	FETCH_ALL_CONTACT_VIEWS_REQUESTED = "CONTACTS_FETCH_ALL_VIEWS_REQUESTED",
	FETCH_ALL_CONTACT_VIEWS_SUCCESSFUL = "CONTACTS_FETCH_ALL_VIEWS_SUCCESSFUL",
	FETCH_ALL_CONTACT_VIEWS_FAILED = "CONTACTS_FETCH_ALL_VIEWS_FAILED",

	FETCH_CONTACT_VIEW_METADATA_REQUESTED = "CONTACTS_FETCH_VIEW_METADATA_REQUESTED",
	FETCH_CONTACT_VIEW_METADATA_SUCCESSFUL = "CONTACTS_FETCH_VIEW_METADATA_SUCCESSFUL",
	FETCH_CONTACT_VIEW_METADATA_FAILED = "CONTACTS_FETCH_VIEW_METADATA_FAILED",

	CREATE_CONTACT_VIEW_REQUESTED = "CONTACTS_CREATE_VIEW_REQUESTED",
	CREATE_CONTACT_VIEW_SUCCESSFUL = "CONTACTS_CREATE_VIEW_SUCCESSFUL",
	CREATE_CONTACT_VIEW_FAILED = "CONTACTS_CREATE_VIEW_FAILED",

	UPDATE_CONTACT_VIEW_REQUESTED = "CONTACTS_UPDATE_VIEW_REQUESTED",
	UPDATE_CONTACT_VIEW_SUCCESSFUL = "CONTACTS_UPDATE_VIEW_SUCCESSFUL",
	UPDATE_CONTACT_VIEW_FAILED = "CONTACTS_UPDATE_VIEW_FAILED",

	DELETE_CONTACT_VIEW_REQUESTED = "CONTACTS_DELETE_VIEW_REQUESTED",
	DELETE_CONTACT_VIEW_SUCCESSFUL = "CONTACTS_DELETE_VIEW_SUCCESSFUL",
	DELETE_CONTACT_VIEW_FAILED = "CONTACTS_DELETE_VIEW_FAILED",

	UPDATE_CONTACTS_CURRENT_ACTIVE_VIEW = "CONTACTS_UPDATE_CURRENT_ACTIVE_VIEW",
	RESET_CONTACTS_CURRENT_ACTIVE_VIEW = "CONTACTS_RESET_CURRENT_ACTIVE_VIEW",

	BULK_ACTION_RECORDS_SELECTED = "CONTACTS_BULK_ACTION_RECORDS_SELECTED",
	BULK_ACTION_RECORDS_RESET = "CONTACTS_BULK_ACTION_RECORDS_RESET",

	BULK_DELETE_RECORDS_REQUESTED = "CONTACTS_BULK_DELETE_RECORDS_REQUESTED",
	BULK_DELETE_RECORDS_SUCCESSFUL = "CONTACTS_BULK_DELETE_RECORDS_SUCCESSFUL",
	BULK_DELETE_RECORDS_FAILED = "CONTACTS_BULK_DELETE_RECORDS_FAILED",

	BULK_UPDATE_RECORDS_REQUESTED = "CONTACTS_BULK_UPDATE_RECORDS_REQUESTED",
	BULK_UPDATE_RECORDS_SUCCESSFUL = "CONTACTS_BULK_UPDATE_RECORDS_SUCCESSFUL",
	BULK_UPDATE_RECORDS_FAILED = "CONTACTS_BULK_UPDATE_RECORDS_FAILED",

	ADD_CONTACT_DRAWER_OPENED = "CONTACTS_ADD_DRAWER_OPENED",
	ADD_CONTACT_DRAWER_CLOSED = "CONTACTS_ADD_DRAWER_CLOSED",
	EDIT_CONTACT_DRAWER_OPENED = "CONTACTS_EDIT_DRAWER_OPENED",
	EDIT_CONTACT_DRAWER_CLOSED = "CONTACTS_EDIT_DRAWER_CLOSED",

	MERGE_RECENT_OPPORTUNITY_FOR_CONTACTS = "CONTACTS_MERGE_MOST_RECENT_OPPORTUNITY",
	MERGE_RECENT_DOCUMENT_FOR_CONTACTS = "CONTACTS_MERGE_MOST_RECENT_DOCUMENT"
}

interface ShowContactsLoader {
	type: ContactsActionTypes.SHOW_CONTACTS_LOADER;
}
interface HideContactsLoader {
	type: ContactsActionTypes.HIDE_CONTACTS_LOADER;
}
interface FetchContactsRequested {
	type: ContactsActionTypes.FETCH_CONTACTS_REQUESTED;
}
interface FetchContactsSuccessful {
	type: ContactsActionTypes.FETCH_CONTACTS_SUCCESSFUL;
	contactList: ClientContactsResponse;
}
interface FetchContactsFailed {
	type: ContactsActionTypes.FETCH_CONTACTS_FAILED;
}
interface FetchAllContactViewsRequested {
	type: ContactsActionTypes.FETCH_ALL_CONTACT_VIEWS_REQUESTED;
}
interface FetchAllContactViewsSuccessful {
	type: ContactsActionTypes.FETCH_ALL_CONTACT_VIEWS_SUCCESSFUL;
	viewList: CustomViewResponse;
}
interface FetchAllContactViewsFailed {
	type: ContactsActionTypes.FETCH_ALL_CONTACT_VIEWS_FAILED;
}
interface FetchContactViewMetadataRequested {
	type: ContactsActionTypes.FETCH_CONTACT_VIEW_METADATA_REQUESTED;
}
interface FetchContactViewMetadataSuccessful {
	type: ContactsActionTypes.FETCH_CONTACT_VIEW_METADATA_SUCCESSFUL;
	metadata: CustomViewsMetadata;
}
interface FetchContactViewMetadataFailed {
	type: ContactsActionTypes.FETCH_CONTACT_VIEW_METADATA_FAILED;
}
interface CreateContactViewRequested {
	type: ContactsActionTypes.CREATE_CONTACT_VIEW_REQUESTED;
}
interface CreateContactViewSuccessful {
	type: ContactsActionTypes.CREATE_CONTACT_VIEW_SUCCESSFUL;
	view: CustomView;
}
interface CreateContactViewFailed {
	type: ContactsActionTypes.CREATE_CONTACT_VIEW_FAILED;
}
interface UpdateContactViewRequested {
	type: ContactsActionTypes.UPDATE_CONTACT_VIEW_REQUESTED;
}
interface UpdateContactViewSuccessful {
	type: ContactsActionTypes.UPDATE_CONTACT_VIEW_SUCCESSFUL;
	view: CustomView;
}
interface UpdateContactViewFailed {
	type: ContactsActionTypes.UPDATE_CONTACT_VIEW_FAILED;
}
interface DeleteContactViewRequested {
	type: ContactsActionTypes.DELETE_CONTACT_VIEW_REQUESTED;
}
interface DeleteContactViewSuccessful {
	type: ContactsActionTypes.DELETE_CONTACT_VIEW_SUCCESSFUL;
	deletedView: CustomView;
	initialView: CustomView;
}
interface DeleteContactViewFailed {
	type: ContactsActionTypes.DELETE_CONTACT_VIEW_FAILED;
}
interface UpdateCurrentActiveViewAction {
	type: ContactsActionTypes.UPDATE_CONTACTS_CURRENT_ACTIVE_VIEW;
	currentActiveView: CustomView;
	isViewChanged: boolean;
}
interface ResetCurrentActiveViewAction {
	type: ContactsActionTypes.RESET_CONTACTS_CURRENT_ACTIVE_VIEW;
}
interface BulkActionRecordsSelected {
	type: ContactsActionTypes.BULK_ACTION_RECORDS_SELECTED;
}
interface BulkActionRecordsReset {
	type: ContactsActionTypes.BULK_ACTION_RECORDS_RESET;
}
interface BulkDeleteRecordsRequested {
	type: ContactsActionTypes.BULK_DELETE_RECORDS_REQUESTED;
}
interface BulkDeleteRecordsSuccessful {
	type: ContactsActionTypes.BULK_DELETE_RECORDS_SUCCESSFUL;
}
interface BulkDeleteRecordsFailed {
	type: ContactsActionTypes.BULK_DELETE_RECORDS_FAILED;
}
interface BulkUpdateRecordsRequested {
	type: ContactsActionTypes.BULK_UPDATE_RECORDS_REQUESTED;
}
interface BulkUpdateRecordsSuccessful {
	type: ContactsActionTypes.BULK_UPDATE_RECORDS_SUCCESSFUL;
}
interface BulkUpdateRecordsFailed {
	type: ContactsActionTypes.BULK_UPDATE_RECORDS_FAILED;
}
interface AddContactDrawerOpened {
	type: ContactsActionTypes.ADD_CONTACT_DRAWER_OPENED;
}
interface AddContactDrawerClosed {
	type: ContactsActionTypes.ADD_CONTACT_DRAWER_CLOSED;
}
interface EditContactDrawerOpened {
	type: ContactsActionTypes.EDIT_CONTACT_DRAWER_OPENED;
	contact: ClientContact;
}
interface EditContactDrawerClosed {
	type: ContactsActionTypes.EDIT_CONTACT_DRAWER_CLOSED;
}

export interface MergeRecentOpportunityForContactsAction {
	type: ContactsActionTypes.MERGE_RECENT_OPPORTUNITY_FOR_CONTACTS;
	map: Record<string, RecentOpportunityItemView | null>;
}
export interface MergeRecentDocumentForContactsAction {
	type: ContactsActionTypes.MERGE_RECENT_DOCUMENT_FOR_CONTACTS;
	map: Record<string, RecentDocumentItemView | null>;
}

export type ContactsActions =
	| ShowContactsLoader
	| HideContactsLoader
	| FetchContactsRequested
	| FetchContactsSuccessful
	| FetchContactsFailed
	| FetchAllContactViewsRequested
	| FetchAllContactViewsSuccessful
	| FetchAllContactViewsFailed
	| FetchContactViewMetadataRequested
	| FetchContactViewMetadataSuccessful
	| FetchContactViewMetadataFailed
	| CreateContactViewRequested
	| CreateContactViewSuccessful
	| CreateContactViewFailed
	| UpdateContactViewRequested
	| UpdateContactViewSuccessful
	| UpdateContactViewFailed
	| DeleteContactViewRequested
	| DeleteContactViewSuccessful
	| DeleteContactViewFailed
	| UpdateCurrentActiveViewAction
	| ResetCurrentActiveViewAction
	| BulkActionRecordsSelected
	| BulkActionRecordsReset
	| BulkDeleteRecordsRequested
	| BulkDeleteRecordsSuccessful
	| BulkDeleteRecordsFailed
	| BulkUpdateRecordsRequested
	| BulkUpdateRecordsSuccessful
	| BulkUpdateRecordsFailed
	| AddContactDrawerOpened
	| AddContactDrawerClosed
	| EditContactDrawerOpened
	| EditContactDrawerClosed
	| MergeRecentOpportunityForContactsAction
	| MergeRecentDocumentForContactsAction;

let nextContactsRequestId = 0;
let latestActiveContactsRequestId = 0;

export const ContactsActionCreators = {
	showContactsLoader: () => {
		return (dispatch: Dispatch): ShowContactsLoader =>
			dispatch({ type: ContactsActionTypes.SHOW_CONTACTS_LOADER });
	},

	hideContactsLoader: () => {
		return (dispatch: Dispatch): HideContactsLoader =>
			dispatch({ type: ContactsActionTypes.HIDE_CONTACTS_LOADER });
	},

	fetchAllContacts: (contactRequest: ListPagePostRequest) => {
		return async (
			dispatch: Dispatch
		): Promise<FetchContactsSuccessful | void> => {
			const requestId = ++nextContactsRequestId;
			latestActiveContactsRequestId = requestId;
			const requested: FetchContactsRequested = {
				type: ContactsActionTypes.FETCH_CONTACTS_REQUESTED
			};
			dispatch(requested);
			try {
				const response = await getContactListPost(contactRequest);
				if (requestId !== latestActiveContactsRequestId) {
					return;
				}
				const successful: FetchContactsSuccessful = {
					type: ContactsActionTypes.FETCH_CONTACTS_SUCCESSFUL,
					contactList: response
				};
				return dispatch(successful);
			} catch (error) {
				if (requestId !== latestActiveContactsRequestId) {
					return;
				}
				const failed: FetchContactsFailed = {
					type: ContactsActionTypes.FETCH_CONTACTS_FAILED
				};
				dispatch(failed);
				return Promise.reject(error);
			}
		};
	},

	fetchAllContactViews: () => {
		return async (
			dispatch: Dispatch
		): Promise<FetchAllContactViewsSuccessful> => {
			const requested: FetchAllContactViewsRequested = {
				type: ContactsActionTypes.FETCH_ALL_CONTACT_VIEWS_REQUESTED
			};
			dispatch(requested);
			try {
				const response = await getAllContactViews();
				const successful: FetchAllContactViewsSuccessful = {
					type: ContactsActionTypes.FETCH_ALL_CONTACT_VIEWS_SUCCESSFUL,
					viewList: response
				};
				return dispatch(successful);
			} catch (error) {
				console.error(error);
				const failed: FetchAllContactViewsFailed = {
					type: ContactsActionTypes.FETCH_ALL_CONTACT_VIEWS_FAILED
				};
				dispatch(failed);
				return Promise.reject(error);
			}
		};
	},

	fetchContactViewMetadata: () => {
		return async (
			dispatch: Dispatch
		): Promise<FetchContactViewMetadataSuccessful> => {
			const requested: FetchContactViewMetadataRequested = {
				type: ContactsActionTypes.FETCH_CONTACT_VIEW_METADATA_REQUESTED
			};
			dispatch(requested);
			try {
				const response = await getContactViewMetadata();
				const successful: FetchContactViewMetadataSuccessful = {
					type: ContactsActionTypes.FETCH_CONTACT_VIEW_METADATA_SUCCESSFUL,
					metadata: response
				};
				return dispatch(successful);
			} catch (error) {
				console.error(error);
				const failed: FetchContactViewMetadataFailed = {
					type: ContactsActionTypes.FETCH_CONTACT_VIEW_METADATA_FAILED
				};
				dispatch(failed);
				return Promise.reject(error);
			}
		};
	},

	updateCurrentActiveView: (
		selectedCurrentActiveView: CustomView,
		discardingView?: boolean
	) => {
		return (
			dispatch: Dispatch,
			getState: () => GlobalState
		): UpdateCurrentActiveViewAction => {
			const currentActiveView = getState().contacts.currentActiveView;
			const prevViewData = getState().contacts.viewList?.data.find(
				view => view.id === selectedCurrentActiveView?.id
			);

			const isSameView =
				currentActiveView?.id === selectedCurrentActiveView?.id;

			if (isSameView && prevViewData?.id) {
				const hasFiltersChanged = areFiltersChanged(
					prevViewData.filter_criteria,
					selectedCurrentActiveView.filter_criteria
				);
				const hasColumnsChanged =
					JSON.stringify(selectedCurrentActiveView.columns) !==
					JSON.stringify(prevViewData.columns);

				return dispatch({
					type: ContactsActionTypes.UPDATE_CONTACTS_CURRENT_ACTIVE_VIEW,
					currentActiveView: selectedCurrentActiveView,
					isViewChanged: discardingView
						? false
						: hasFiltersChanged || hasColumnsChanged
				});
			}

			return dispatch({
				type: ContactsActionTypes.UPDATE_CONTACTS_CURRENT_ACTIVE_VIEW,
				currentActiveView: selectedCurrentActiveView,
				isViewChanged: false
			});
		};
	},

	resetCurrentActiveView: () => {
		return (dispatch: Dispatch): ResetCurrentActiveViewAction =>
			dispatch({
				type: ContactsActionTypes.RESET_CONTACTS_CURRENT_ACTIVE_VIEW
			});
	},

	createContactView: (view: CustomView) => {
		return async (
			dispatch: Dispatch
		): Promise<CreateContactViewSuccessful> => {
			const requested: CreateContactViewRequested = {
				type: ContactsActionTypes.CREATE_CONTACT_VIEW_REQUESTED
			};
			dispatch(requested);
			try {
				const response = await createContactViewService(view);
				const successful: CreateContactViewSuccessful = {
					type: ContactsActionTypes.CREATE_CONTACT_VIEW_SUCCESSFUL,
					view: response
				};
				return dispatch(successful);
			} catch (error) {
				console.error(error);
				const failed: CreateContactViewFailed = {
					type: ContactsActionTypes.CREATE_CONTACT_VIEW_FAILED
				};
				dispatch(failed);
				return Promise.reject(error);
			}
		};
	},

	updateContactView: (view: CustomView) => {
		return async (
			dispatch: Dispatch
		): Promise<UpdateContactViewSuccessful> => {
			const requested: UpdateContactViewRequested = {
				type: ContactsActionTypes.UPDATE_CONTACT_VIEW_REQUESTED
			};
			dispatch(requested);
			try {
				const response = await updateContactViewService(view);
				const successful: UpdateContactViewSuccessful = {
					type: ContactsActionTypes.UPDATE_CONTACT_VIEW_SUCCESSFUL,
					view: response
				};
				return dispatch(successful);
			} catch (error) {
				console.error(error);
				const failed: UpdateContactViewFailed = {
					type: ContactsActionTypes.UPDATE_CONTACT_VIEW_FAILED
				};
				dispatch(failed);
				return Promise.reject(error);
			}
		};
	},

	deleteContactView: (deleteView: CustomView, initialView: CustomView) => {
		return async (
			dispatch: Dispatch
		): Promise<DeleteContactViewSuccessful> => {
			const requested: DeleteContactViewRequested = {
				type: ContactsActionTypes.DELETE_CONTACT_VIEW_REQUESTED
			};
			dispatch(requested);
			try {
				await deleteContactViewService(deleteView.id);
				const successful: DeleteContactViewSuccessful = {
					type: ContactsActionTypes.DELETE_CONTACT_VIEW_SUCCESSFUL,
					deletedView: deleteView,
					initialView
				};
				return dispatch(successful);
			} catch (error) {
				console.error(error);
				const failed: DeleteContactViewFailed = {
					type: ContactsActionTypes.DELETE_CONTACT_VIEW_FAILED
				};
				dispatch(failed);
				return Promise.reject(error);
			}
		};
	},

	bulkActionsRecordsSelected: () => {
		return (dispatch: Dispatch): BulkActionRecordsSelected =>
			dispatch({
				type: ContactsActionTypes.BULK_ACTION_RECORDS_SELECTED
			});
	},

	bulkActionsRecordsReset: () => {
		return (dispatch: Dispatch): BulkActionRecordsReset =>
			dispatch({ type: ContactsActionTypes.BULK_ACTION_RECORDS_RESET });
	},

	bulkDeleteRecords: (deleteRecordsRequest: BulkDeleteRecordsRequest) => {
		return async (
			dispatch: Dispatch
		): Promise<BulkDeleteRecordsSuccessful> => {
			const requested: BulkDeleteRecordsRequested = {
				type: ContactsActionTypes.BULK_DELETE_RECORDS_REQUESTED
			};
			dispatch(requested);
			try {
				await bulkDeleteContacts(deleteRecordsRequest);
				const successful: BulkDeleteRecordsSuccessful = {
					type: ContactsActionTypes.BULK_DELETE_RECORDS_SUCCESSFUL
				};
				return dispatch(successful);
			} catch (error) {
				console.error(error);
				const failed: BulkDeleteRecordsFailed = {
					type: ContactsActionTypes.BULK_DELETE_RECORDS_FAILED
				};
				dispatch(failed);
				return Promise.reject(error);
			}
		};
	},

	bulkUpdateRecords: (updateRecordsRequest: BulkUpdateRecordsRequest) => {
		return async (
			dispatch: Dispatch
		): Promise<BulkUpdateRecordsSuccessful> => {
			const requested: BulkUpdateRecordsRequested = {
				type: ContactsActionTypes.BULK_UPDATE_RECORDS_REQUESTED
			};
			dispatch(requested);
			try {
				await bulkUpdateContacts(updateRecordsRequest);
				const successful: BulkUpdateRecordsSuccessful = {
					type: ContactsActionTypes.BULK_UPDATE_RECORDS_SUCCESSFUL
				};
				return dispatch(successful);
			} catch (error) {
				console.error(error);
				const failed: BulkUpdateRecordsFailed = {
					type: ContactsActionTypes.BULK_UPDATE_RECORDS_FAILED
				};
				dispatch(failed);
				return Promise.reject(error);
			}
		};
	},

	addContactDrawerOpened: () => {
		return (dispatch: Dispatch): AddContactDrawerOpened =>
			dispatch({ type: ContactsActionTypes.ADD_CONTACT_DRAWER_OPENED });
	},

	addContactDrawerClosed: () => {
		return (dispatch: Dispatch): AddContactDrawerClosed =>
			dispatch({ type: ContactsActionTypes.ADD_CONTACT_DRAWER_CLOSED });
	},

	editContactDrawerOpened: (contact: ClientContact) => {
		return (dispatch: Dispatch): EditContactDrawerOpened =>
			dispatch({
				type: ContactsActionTypes.EDIT_CONTACT_DRAWER_OPENED,
				contact
			});
	},

	editContactDrawerClosed: () => {
		return (dispatch: Dispatch): EditContactDrawerClosed =>
			dispatch({
				type: ContactsActionTypes.EDIT_CONTACT_DRAWER_CLOSED
			});
	},

	fetchRecentOpportunityByContactIds: (contactIds: string[]) => {
		return async (
			dispatch: Dispatch
		): Promise<MergeRecentOpportunityForContactsAction> => {
			const map: Record<string, RecentOpportunityItemView | null> = {};
			if (!contactIds.length) {
				const emptyAction: MergeRecentOpportunityForContactsAction = {
					type: ContactsActionTypes.MERGE_RECENT_OPPORTUNITY_FOR_CONTACTS,
					map
				};
				return dispatch(emptyAction);
			}
			contactIds.forEach(id => {
				map[id] = null;
			});
			try {
				const response = await getRecentOpportunitiesByClientUserIds(
					contactIds
				);
				response.data.forEach(item => {
					if (item.primary_client_user_id) {
						map[item.primary_client_user_id] = item;
					}
				});
			} catch (error) {
				console.error(error);
			}
			const action: MergeRecentOpportunityForContactsAction = {
				type: ContactsActionTypes.MERGE_RECENT_OPPORTUNITY_FOR_CONTACTS,
				map
			};
			return dispatch(action);
		};
	},

	fetchRecentDocumentByContactIds: (contactIds: string[]) => {
		return async (
			dispatch: Dispatch
		): Promise<MergeRecentDocumentForContactsAction> => {
			const map: Record<string, RecentDocumentItemView | null> = {};
			if (!contactIds.length) {
				const emptyAction: MergeRecentDocumentForContactsAction = {
					type: ContactsActionTypes.MERGE_RECENT_DOCUMENT_FOR_CONTACTS,
					map
				};
				return dispatch(emptyAction);
			}
			contactIds.forEach(id => {
				map[id] = null;
			});
			try {
				const response = await getRecentDocumentsByClientUserIds(
					contactIds
				);
				const requestedSet = new Set(contactIds);
				response.data.forEach(item => {
					item.recipients.forEach(recipient => {
						if (requestedSet.has(recipient.recipient_id)) {
							map[recipient.recipient_id] = item;
						}
					});
				});
			} catch (error) {
				console.error(error);
			}
			const action: MergeRecentDocumentForContactsAction = {
				type: ContactsActionTypes.MERGE_RECENT_DOCUMENT_FOR_CONTACTS,
				map
			};
			return dispatch(action);
		};
	}
};
